<script setup>
import { computed } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { usePortalStore, infoBoxIdEnum } from '@/stores/portal.store'
import { useNotifications } from '@/composables/notifications'

import InfoBox from '@/components/shared/InfoBox.vue'

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const portalStore = usePortalStore()
const { notificationTypeName, notificationTypeHint } = useNotifications()

const frequencies = computed(() => portalStore.getLocalizedNotificationFrequencies())
const reminders = computed(() => portalStore.getLocalizedNotificationReminders())

const groups = computed(() => portalStore.getLocalizedNotificationCategories()
  .map(category => ({ category, rows: props.modelValue.filter(row => row.category === category.id) }))
  .filter(group => group.rows.length > 0))

const nameOf = (items, id) => items.find(item => item.id === id)?.name
const receiveButton = ({ enabled }) => (enabled ? { icon: 'mdi-bell-ring-outline', color: 'primary' } : { icon: 'mdi-bell-off-outline', color: 'grey-5' })
const hasReminder = (row) => row.reminder !== portalStore.notificationReminderEnum.NEVER

const update = (row, changes) => emit('update:modelValue', props.modelValue.map(item => (item.type === row.type ? { ...item, ...changes } : item)))
</script>

<template>
  <q-card-section class="q-pt-none">
    <q-item-label
      header
      class="q-px-none row items-center"
    >
      <q-icon
        name="mdi-bell-cog-outline"
        class="q-mr-sm"
        color="primary"
        size="sm"
      />
      <span class="dialog-card-section-title">
        {{ i18n.t('menu.notifications') }}
      </span>
    </q-item-label>
    <div class="notification-settings">
      <div class="matrix-table-scroll">
        <table class="matrix-table notification-settings-table">
          <thead>
            <tr>
              <th class="label-col">{{ i18n.t('common.type') }}</th>
              <th>{{ i18n.t('common.receive') }}</th>
              <th>{{ i18n.t('common.frequency') }}</th>
              <th>{{ i18n.t('common.reminder') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in groups" :key="group.category.id">
              <tr class="group-row">
                <th class="label-col dialog-card-section-title">{{ group.category.name }}</th>
                <td colspan="3" />
              </tr>
              <tr
                v-for="row in group.rows"
                :key="row.type"
                :class="['data-row', { 'row-off': !row.enabled }]"
              >
                <th class="label-col">
                  <span>{{ notificationTypeName(row) }}</span>
                  <span class="label-sub">{{ notificationTypeHint(row) }}</span>
                </th>
                <td>
                  <q-btn
                    flat
                    round
                    dense
                    v-bind="receiveButton(row)"
                    @click="update(row, { enabled: !row.enabled })"
                  />
                </td>
                <td>
                  <template v-if="row.enabled">
                    <q-chip
                      size="sm"
                      clickable
                      color="primary"
                      text-color="white"
                      icon="mdi-timer-outline"
                      :label="nameOf(frequencies, row.frequency)"
                    />
                    <q-menu auto-close>
                      <q-list dense>
                        <q-item
                          v-for="frequency in frequencies"
                          :key="frequency.id"
                          clickable
                          :active="frequency.id === row.frequency"
                          @click="update(row, { frequency: frequency.id })"
                        >
                          <q-item-section>{{ frequency.name }}</q-item-section>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </template>
                  <span v-else class="matrix-cell-none">—</span>
                </td>
                <td>
                  <template v-if="row.enabled && row.persistent">
                    <q-chip
                      v-if="hasReminder(row)"
                      size="sm"
                      clickable
                      color="teal"
                      text-color="white"
                      icon="mdi-repeat"
                      :label="nameOf(reminders, row.reminder)"
                    />
                    <q-btn
                      v-else
                      flat
                      dense
                      class="matrix-cell-none"
                      label="—"
                    />
                    <q-menu auto-close>
                      <q-list dense>
                        <q-item
                          v-for="reminder in reminders"
                          :key="reminder.id"
                          clickable
                          :active="reminder.id === row.reminder"
                          @click="update(row, { reminder: reminder.id })"
                        >
                          <q-item-section>{{ reminder.name }}</q-item-section>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </template>
                  <span v-else class="matrix-cell-none">—</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <info-box
        :id="infoBoxIdEnum.NOTIFICATION_SETTINGS"
        :message="i18n.t('common.notificationSettingsHint')"
      />
    </div>
  </q-card-section>
</template>

<style lang="scss" scoped>
.notification-settings {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
}

.notification-settings-table {
  min-width: 40rem;

  .row-off th.label-col {
    opacity: .55;
  }
}
</style>
